import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import { DatePicker, TextField, Select } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $materialsReconciliationForm,
  $updateMaterialsReconciliationForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { calculateDueDate } from '~/modules/trade/utils/paymentTerms';

const INVOICE_TYPE_OPTIONS = [
  { key: 'from_shipped_materials', label: 'From Shipped Materials' },
  { key: 'from_received_materials', label: 'From Received Materials' },
];

export const InvoiceDetails = () => {
  const t = useTranslations();
  const formState = useAtomValue($materialsReconciliationForm);
  const updateForm = useSetAtom($updateMaterialsReconciliationForm);

  const handleInvoiceDateChange = (value: DateValue | null) => {
    updateForm({
      invoiceDate: value
        ? new CalendarDate(value.year, value.month, value.day)
        : undefined,
    });
  };

  const handleDueDateChange = (value: DateValue | null) => {
    updateForm({
      dueDate: value
        ? new CalendarDate(value.year, value.month, value.day)
        : undefined,
    });
  };

  const handleInvoiceTypeChange = (value: string | undefined) => {
    updateForm({ invoiceType: value });
  };

  const handleExternalNotesChange = (value: string) => {
    updateForm({ externalNotes: value });
  };

  const handleInternalNotesChange = (value: string) => {
    updateForm({ internalNotes: value });
  };

  React.useEffect(() => {
    const initialUpdates: Partial<typeof formState> = {};
    if (!formState.invoiceType) {
      initialUpdates.invoiceType = 'from_shipped_materials';
    }
    if (!formState.invoiceDate) {
      const now = new Date();
      initialUpdates.invoiceDate = new CalendarDate(
        now.getFullYear(),
        now.getMonth() + 1,
        now.getDate(),
      );
    }

    if (Object.keys(initialUpdates).length > 0) {
      updateForm(initialUpdates);
    }
  }, [updateForm]);

  // Auto-calculate due date when payment term or invoice date changes
  React.useEffect(() => {
    if (formState.paymentTerm && formState.invoiceDate) {
      const calculatedDueDate = calculateDueDate(
        formState.paymentTerm,
        formState.invoiceDate,
      );
      updateForm({ dueDate: calculatedDueDate });
    }
  }, [formState.paymentTerm, formState.invoiceDate, updateForm]);

  return (
    <div className="@InvoiceDetails p-6">
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <DatePicker
            value={formState.invoiceDate}
            label={t.materialsReconciliation.invoiceDetails.invoiceDate}
            onChange={handleInvoiceDateChange}
            isRequired
            className="[&_[data-slot=field-group]]:bg-white [&_[role=group]]:bg-white"
          />
          <DatePicker
            value={formState.dueDate}
            label={t.materialsReconciliation.invoiceDetails.dueDate}
            onChange={handleDueDateChange}
            className="[&_[data-slot=field-group]]:bg-white [&_[role=group]]:bg-white"
          />
          <Select
            value={formState.invoiceType}
            onChange={handleInvoiceTypeChange}
            label={t.materialsReconciliation.invoiceDetails.invoiceType}
            isRequired
            placeholder={
              t.materialsReconciliation.invoiceDetails.selectInvoiceType
            }
            options={INVOICE_TYPE_OPTIONS}
          />
        </div>

        <div className="flex w-full flex-row justify-between gap-4">
          <TextField
            value={formState.externalNotes}
            label={t.materialsReconciliation.invoiceDetails.externalNotes}
            onChange={handleExternalNotesChange}
            placeholder={
              t.materialsReconciliation.invoiceDetails.externalNotesPlaceholder
            }
            className="col-span-4 w-full [&_input]:bg-white [&_textarea]:bg-white"
            textArea
          />
          <TextField
            value={formState.internalNotes}
            label={t.materialsReconciliation.invoiceDetails.internalNotes}
            onChange={handleInternalNotesChange}
            placeholder={
              t.materialsReconciliation.invoiceDetails.internalNotesPlaceholder
            }
            className="col-span-4 w-full [&_input]:bg-white [&_textarea]:bg-white"
            textArea
          />
        </div>
      </div>
    </div>
  );
};
